import React, { useState, useEffect, useRef } from 'react';
import "../../css/Profile.css";
import { User, Mail, Phone, MapPin, Calendar, Edit, Camera, X, Eye, EyeOff, Lock, Shield } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from "../../components/Loader";

function Profile() {
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Default profile icon SVG
  const defaultProfileIcon = `data:image/svg+xml;base64,${btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="150" height="150" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  `)}`;
  
  const [admin, setAdmin] = useState({
    name: "",
    role: "",
    email: "",
    phone: "",
    location: "",
    joinDate: "",
    bio: "",
    profileImage: defaultProfileIcon,
    twoFactorEnabled: false
  });

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({ ...admin });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // QR code state for 2FA
  const [qrCodeUrl, setQrCodeUrl] = useState('/api/placeholder/200/200');
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  // Utility function to enforce minimum loader display time
  const withMinimumLoader = async (operation, minTime = 500) => {
    setIsLoading(true);
    const startTime = Date.now();
    try {
      const result = await operation();
      const elapsed = Date.now() - startTime;
      if (elapsed < minTime) {
        await new Promise(resolve => setTimeout(resolve, minTime - elapsed));
      }
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch admin profile data
  useEffect(() => {
    fetchAdminProfile();
  }, []);

  const fetchAdminProfile = async () => {
    await withMinimumLoader(async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Unauthorized! Please login again.");
          return;
        }

        const response = await fetch("https://schoolpro-api.uniums.com/api/profile/profile", {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch profile");
        }

        const data = await response.json();
        if (data.success) {
          const profileImage = data.admin.profileImage || defaultProfileIcon;
          const updatedAdmin = {
            ...data.admin,
            profileImage,
            phone: data.admin.phone || "",
            location: data.admin.location || "",
            bio: data.admin.bio || "",
            role: data.admin.role || "System Administrator"
          };
          
          setAdmin(updatedAdmin);
          setFormData(updatedAdmin);
        } else {
          throw new Error(data.message || "Failed to fetch profile");
        }
      } catch (error) {
        console.error("Error fetching admin profile:", error);
        toast.error(`Error: ${error.message}`);
      }
    });
  };

  // Generate 2FA QR code
  const generateTwoFactorQR = async () => {
    await withMinimumLoader(async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Unauthorized! Please login again.");
          return;
        }

        const response = await fetch("https://schoolpro-api.uniums.com/api/profile/generate-2fa-qr", {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to generate QR code");
        }

        const data = await response.json();
        if (data.success) {
          setQrCodeUrl(data.qrCodeUrl);
          setTwoFactorSecret(data.secret);
        } else {
          throw new Error(data.message || "Failed to generate QR code");
        }
      } catch (error) {
        console.error("Error generating 2FA QR code:", error);
        toast.error(`Error: ${error.message}`);
      }
    });
  };

  // Handle profile image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a JPEG or PNG image");
      return;
    }

    await withMinimumLoader(async () => {
      try {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        await new Promise((resolve, reject) => {
          reader.onload = () => resolve();
          reader.onerror = () => reject(new Error("Error reading image file"));
        });

        const imageData = reader.result;
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Unauthorized! Please login again.");
          return;
        }

        const response = await fetch("https://schoolpro-api.uniums.com/api/profile/profile/upload-image", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ imageData })
        });

        const data = await response.json();
        if (response.ok && data.success) {
          setAdmin(prev => ({
            ...prev,
            profileImage: data.profileImage
          }));
          setFormData(prev => ({
            ...prev,
            profileImage: data.profileImage
          }));
          toast.success("Profile image updated successfully");
        } else {
          toast.error(data.message || "Failed to upload image");
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error(`Error: ${error.message}`);
      }
    });
  };

  // Open edit modal with pre-filled data
  const openEditModal = () => {
    setFormData({ ...admin });
    setShowEditModal(true);
  };

  // Trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle password form changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle 2FA verification code input
  const handleVerificationCodeChange = (e) => {
    setVerificationCode(e.target.value);
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Handle form submission for profile update
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    await withMinimumLoader(async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Unauthorized! Please login again.");
          return;
        }

        const response = await fetch("https://schoolpro-api.uniums.com/api/profile/profile", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            role: formData.role,
            location: formData.location,
            bio: formData.bio
          })
        });

        const data = await response.json();
        if (response.ok && data.success) {
          setAdmin(prev => ({
            ...prev,
            ...formData,
            profileImage: prev.profileImage // Preserve profile image
          }));
          toast.success("Profile updated successfully");
          setShowEditModal(false);
        } else {
          toast.error(data.message || "Failed to update profile");
        }
      } catch (error) {
        console.error("Error updating profile:", error);
        toast.error(`Error: ${error.message}`);
      }
    });
  };

  // Handle password update
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords don't match!");
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long!");
      return;
    }
    
    await withMinimumLoader(async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Unauthorized! Please login again.");
          return;
        }

        const response = await fetch("https://schoolpro-api.uniums.com/api/profile/change-password", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            currentPassword: passwordData.currentPassword,
            newPassword: passwordData.newPassword
          })
        });

        const data = await response.json();
        if (response.ok && data.success) {
          toast.success("Password updated successfully!");
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          setShowPasswordModal(false);
        } else {
          toast.error(data.message || "Failed to update password");
        }
      } catch (error) {
        console.error("Error updating password:", error);
        toast.error(`Error: ${error.message}`);
      }
    });
  };

  // Handle 2FA toggle
  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    await withMinimumLoader(async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Unauthorized! Please login again.");
          return;
        }

        const response = await fetch("https://schoolpro-api.uniums.com/api/profile/setup-2fa", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            verificationCode: verificationCode
          })
        });

        const data = await response.json();
        if (response.ok && data.success) {
          toast.success(data.message);
          setAdmin(prev => ({
            ...prev,
            twoFactorEnabled: data.twoFactorEnabled
          }));
          setShowTwoFactorModal(false);
          setVerificationCode('');
        } else {
          toast.error(data.message || "Failed to setup two-factor authentication");
        }
      } catch (error) {
        console.error("Error setting up 2FA:", error);
        toast.error(`Error: ${error.message}`);
      }
    });
  };

  // Handle opening two-factor modal
  const handleOpenTwoFactorModal = () => {
    generateTwoFactorQR();
    setShowTwoFactorModal(true);
  };

  return (
    <>
      <div className="admin-profile-container">
        <input 
          type="file" 
          ref={fileInputRef} 
          style={{ display: 'none' }} 
          accept="image/jpeg,image/png" 
          onChange={handleImageUpload} 
        />
        
        {isLoading && <Loader />}
        
        <div className="admin-profile-header">
          <h1 className="admin-profile-title">Admin Profile</h1>
          <button 
            className="admin-profile-edit-btn"
            onClick={openEditModal}
            disabled={isLoading}
          >
            <Edit size={18} />
            <span>Edit Profile</span>
          </button>
        </div>

        <div className="admin-profile-content">
          <div className="admin-profile-card">
            <div className="admin-profile-image-section">
              <div className="admin-profile-image-container">
                <img 
                  src={admin.profileImage} 
                  alt="Admin Profile" 
                  className="admin-profile-image" 
                  onError={(e) => { e.target.src = defaultProfileIcon }}
                />
                <div 
                  className="admin-profile-image-overlay"
                  onClick={triggerFileInput}
                >
                  <Camera size={20} />
                </div>
              </div>
              <h2 className="admin-profile-name">{admin.name || "Admin"}</h2>
              <p className="admin-profile-role">{admin.role || "System Administrator"}</p>
            </div>

            <div className="admin-profile-details">
              <div className="admin-profile-info-item">
                <Mail className="admin-profile-icon" size={18} />
                <div>
                  <p className="admin-profile-label">Email</p>
                  <p className="admin-profile-value">{admin.email || "Not provided"}</p>
                </div>
              </div>

              <div className="admin-profile-info-item">
                <Phone className="admin-profile-icon" size={18} />
                <div>
                  <p className="admin-profile-label">Phone</p>
                  <p className="admin-profile-value">{admin.phone || "Not provided"}</p>
                </div>
              </div>

              <div className="admin-profile-info-item">
                <MapPin className="admin-profile-icon" size={18} />
                <div>
                  <p className="admin-profile-label">Location</p>
                  <p className="admin-profile-value">{admin.location || "Not provided"}</p>
                </div>
              </div>

              <div className="admin-profile-info-item">
                <Calendar className="admin-profile-icon" size={18} />
                <div>
                  <p className="admin-profile-label">Join Date</p>
                  <p className="admin-profile-value">{admin.joinDate || "Not available"}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="admin-profile-card">
            <h3 className="admin-profile-section-title">Bio</h3>
            <p className="admin-profile-bio">{admin.bio || "No bio provided yet."}</p>
          </div>

          <div className="admin-profile-card">
            <h3 className="admin-profile-section-title">Account Security</h3>
            <div className="admin-profile-security-options">
              <button 
                className="admin-profile-security-btn"
                onClick={() => setShowPasswordModal(true)}
                disabled={isLoading}
              >
                <Lock size={16} className="admin-profile-btn-icon" />
                Change Password
              </button>
              <button 
                className="admin-profile-security-btn"
                onClick={handleOpenTwoFactorModal}
                disabled={isLoading}
              >
                <Shield size={16} className="admin-profile-btn-icon" />
                {admin.twoFactorEnabled ? "Update Two-Factor Auth" : "Setup Two-Factor Auth"}
              </button>
            </div>
          </div>
        </div>

        {showEditModal && (
          <div className="admin-profile-modal-overlay">
            <div className="admin-profile-modal">
              <div className="admin-profile-modal-header">
                <h3 className="admin-profile-modal-title">Edit Profile</h3>
                <button 
                  className="admin-profile-modal-close"
                  onClick={() => setShowEditModal(false)}
                  disabled={isLoading}
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleEditSubmit} className="admin-profile-form">
                <div className="admin-profile-form-group">
                  <label className="admin-profile-form-label">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="admin-profile-form-input"
                    required
                    placeholder="Enter your name"
                    disabled={isLoading}
                  />
                </div>
                
                <div className="admin-profile-form-row">
                  <div className="admin-profile-form-group">
                    <label className="admin-profile-form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="admin-profile-form-input"
                      required
                      placeholder="Enter your email"
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div className="admin-profile-form-group">
                    <label className="admin-profile-form-label">Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="admin-profile-form-input"
                      placeholder="Enter your phone number"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                
                <div className="admin-profile-form-row">
                  <div className="admin-profile-form-group">
                    <label className="admin-profile-form-label">Role</label>
                    <input
                      type="text"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="admin-profile-form-input"
                      placeholder="Enter your role"
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div className="admin-profile-form-group">
                    <label className="admin-profile-form-label">Location</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      className="admin-profile-form-input"
                      placeholder="Enter your location"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                
                <div className="admin-profile-form-group">
                  <label className="admin-profile-form-label">Bio</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    className="admin-profile-form-textarea"
                    rows="4"
                    placeholder="Tell us about yourself"
                    disabled={isLoading}
                  ></textarea>
                </div>
                
                <div className="admin-profile-form-actions">
                  <button 
                    type="button" 
                    className="admin-profile-btn-cancel"
                    onClick={() => setShowEditModal(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="admin-profile-btn-save"
                    disabled={isLoading}
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPasswordModal && (
          <div className="admin-profile-modal-overlay">
            <div className="admin-profile-modal">
              <div className="admin-profile-modal-header">
                <h3 className="admin-profile-modal-title">Change Password</h3>
                <button 
                  className="admin-profile-modal-close"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={isLoading}
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handlePasswordSubmit} className="admin-profile-form">
                <div className="admin-profile-form-group">
                  <label className="admin-profile-form-label">Current Password</label>
                  <div className="admin-profile-password-input">
                    <input
                      type={showPassword.current ? "text" : "password"}
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      className="admin-profile-form-input"
                      required
                      disabled={isLoading}
                    />
                    <button 
                      type="button"
                      className="admin-profile-password-toggle"
                      onClick={() => togglePasswordVisibility('current')}
                      disabled={isLoading}
                    >
                      {showPassword.current ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                
                <div className="admin-profile-form-group">
                  <label className="admin-profile-form-label">New Password</label>
                  <div className="admin-profile-password-input">
                    <input
                      type={showPassword.new ? "text" : "password"}
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="admin-profile-form-input"
                      required
                      minLength="8"
                      disabled={isLoading}
                    />
                    <button 
                      type="button"
                      className="admin-profile-password-toggle"
                      onClick={() => togglePasswordVisibility('new')}
                      disabled={isLoading}
                    >
                      {showPassword.new ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="admin-profile-form-hint">
                    Password must be at least 8 characters long
                  </p>
                </div>
                
                <div className="admin-profile-form-group">
                  <label className="admin-profile-form-label">Confirm New Password</label>
                  <div className="admin-profile-password-input">
                    <input
                      type={showPassword.confirm ? "text" : "password"}
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="admin-profile-form-input"
                      required
                      disabled={isLoading}
                    />
                    <button 
                      type="button"
                      className="admin-profile-password-toggle"
                      onClick={() => togglePasswordVisibility('confirm')}
                      disabled={isLoading}
                    >
                      {showPassword.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                
                <div className="admin-profile-form-actions">
                  <button 
                    type="button" 
                    className="admin-profile-btn-cancel"
                    onClick={() => setShowPasswordModal(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="admin-profile-btn-save"
                    disabled={isLoading}
                  >
                    Update Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showTwoFactorModal && (
          <div className="admin-profile-modal-overlay">
            <div className="admin-profile-modal">
              <div className="admin-profile-modal-header">
                <h3 className="admin-profile-modal-title">Two-Factor Authentication</h3>
                <button 
                  className="admin-profile-modal-close"
                  onClick={() => setShowTwoFactorModal(false)}
                  disabled={isLoading}
                >
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleTwoFactorSubmit} className="admin-profile-form">
                <div className="admin-profile-tfa-content">
                  <div className="admin-profile-tfa-qr">
                    <img 
                      src={qrCodeUrl} 
                      alt="QR Code" 
                      className="admin-profile-tfa-qr-code" 
                    />
                  </div>
                  <div className="admin-profile-tfa-instructions">
                    <h4 className="admin-profile-tfa-title">Setup Instructions</h4>
                    <ol className="admin-profile-tfa-steps">
                      <li>Download an authenticator app like Google Authenticator or Authy</li>
                      <li>Scan the QR code with your authenticator app</li>
                      <li>Enter the 6-digit code from your authenticator app below</li>
                    </ol>
                    
                    {twoFactorSecret && (
                      <div className="admin-profile-tfa-secret">
                        <p className="admin-profile-form-label">Backup Secret:</p>
                        <p className="admin-profile-tfa-secret-code">{twoFactorSecret}</p>
                        <p className="admin-profile-form-hint">Save this secret somewhere safe in case you lose access to your device.</p>
                      </div>
                    )}
                    
                    <div className="admin-profile-form-group">
                      <label className="admin-profile-form-label">Verification Code</label>
                      <input
                        type="text"
                        className="admin-profile-form-input"
                        placeholder="Enter 6-digit code"
                        maxLength="6"
                        pattern="[0-9]{6}"
                        value={verificationCode}
                        onChange={handleVerificationCodeChange}
                        required
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="admin-profile-form-actions">
                  <button 
                    type="button" 
                    className="admin-profile-btn-cancel"
                    onClick={() => setShowTwoFactorModal(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="admin-profile-btn-save"
                    disabled={isLoading}
                  >
                    {admin.twoFactorEnabled ? "Update" : "Verify & Enable"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </>
  );
}

export default Profile;